import React from 'react';
import PropTypes from 'prop-types';
import { TRACKING_ACTIONS, trackEvent } from '@medflex/utils/Tracking';
import { useTranslation } from 'react-i18next';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import Modal from '../Modal';
import Button from '../Button';
import TextSnippetFile from '../TextSnippetFile';

const TextSnippetsModal = ({ snippets, onSelected, onCreate, onEdit, onClose }) => {
  const { t } = useTranslation('components');

  const handleSelected = ({ id }) => {
    onSelected({ id });
  };

  const handleEdit = event => {
    trackEvent(event, { action: TRACKING_ACTIONS.OPEN, actionText: 'manageTextBlock' });
    onEdit();
  };

  const handleCreate = event => {
    trackEvent(event, { action: TRACKING_ACTIONS.OPEN, actionText: 'manageTextBlock' });
    onCreate();
  };

  return (
    <Modal
      title={t('TextSnippetsModal.title')}
      showCloseButton
      onClickCloseButton={onClose}
      onClickOutside={onClose}
    >
      <div data-component="TextSnippetsModal">
        <header className="flex items-center justify-between">
          <button
            onClick={handleCreate}
            type="button"
            className="mb-4 focus:outline-none text-blue-500 hover:text-blue-600 flex items-center"
          >
            <PlusCircleIcon className="mr-2 h-6 w-6" />
            {t('TextSnippetsModal.create')}
          </button>
        </header>
        <ul className="overflow-hidden">
          {snippets.map(snippet => (
            <li key={snippet.id}>
              <button
                type="button"
                onClick={() => handleSelected({ id: snippet.id })}
                className="group p-4 block w-full mb-4 text-left bg-slate-50 hover:bg-slate-100 outline-none focus:outline-none border border-solid border-slate-200 rounded"
              >
                <div className="text-sm font-semibold text-blue-500 group-hover:text-blue-600 truncate">
                  {snippet.title}
                </div>
                <div className="mb-2 text-sm font-normal text-slate-900 truncate">
                  {snippet.text}
                </div>
                {snippet.files?.length && (
                  <TextSnippetFile
                    mediaType={snippet.files[0].mediaType}
                    name={snippet.files[0].name}
                    additionalFiles={
                      snippet.files.length > 1 ? t('TextSnippetsModal.additionalFiles') : undefined
                    }
                  />
                )}
              </button>
            </li>
          ))}
        </ul>
        <div className="flex lg:justify-end">
          <Button kind="primary:outline" onClick={handleEdit} className="w-full lg:w-1/2">
            <span className="inline">{t('TextSnippetsModal.manage_text_snippets')}</span>
          </Button>
        </div>
      </div>
    </Modal>
  );
};

TextSnippetsModal.propTypes = {
  snippets: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      text: PropTypes.string,
      id: PropTypes.string,
      files: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          mediaType: PropTypes.string,
          title: PropTypes.string,
          id: PropTypes.string,
          bytes: PropTypes.number,
          detectedMediaType: PropTypes.string,
        }),
      ),
    }),
  ),
  onSelected: PropTypes.func,
  onCreate: PropTypes.func,
  onEdit: PropTypes.func,
  onClose: PropTypes.func,
};
TextSnippetsModal.defaultProps = {
  snippets: [],
  onSelected: () => {},
  onCreate: () => {},
  onEdit: () => {},
  onClose: () => {},
};

export default TextSnippetsModal;
